import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';
import Button from '../../../CustomButtons/Button.js';
import { observer } from 'mobx-react';
import { blackColor, whiteColor, btNavyBlueColor } from '../../../../assets/jss/nextjs-material-kit-pro.js';
import CloseIcon from '@material-ui/icons/Close';
import PhoneInput from '../../../Shared/PhoneInput';

const formStyles = makeStyles((theme) => ({
  form: {
    paddingLeft: 20,
    paddingTop: 20,
    width: '100%',
  },
  searchIcon: {
    top: 10,
    right: 33,
  },
  buttonWidth: {
    width: '110px',
  },
  buttonSpacer: {
    marginLeft: 20,
  },
  dialog: {
    width: 500,
    minHeight: 150,
    backgroundColor: whiteColor,
    color: blackColor,
  },
  whiteBG: {
    backgroundColor: whiteColor,
  },
  dialogHeader: {
    backgroundColor: whiteColor,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  search: {
    paddingBottom: 10,
  },
  button: {
    marginLeft: 20,
    marginBottom: 20,
  },
  actions: {
    '& .MuiDialogActions-spacing': {
      justifyContent: 'space-around',
    },
  },
  rowSpacer: {
    marginBottom: '5px',
  },
  fontSize: '0.85rem',
  fontWeight: 500,
  '& svg': {
    width: '1.25rem',
    height: '1.25rem',
  },
  dialogExplain: {
    margin: '0 15px 10px 25px',
    fontSize: '0.90rem',
    fontWeight: 500,
  },
  labelInlineEditSpacer: {
    [theme.breakpoints.down('md')]: {
      height: 40,
      paddingTop: 3,
    },
    [theme.breakpoints.up('md')]: {
      height: 45,
      paddingTop: 5,
    },
  },
  filterOptionGroup: {
    minWidth: '99%',
  },
  radioLabel: {
    fontSize: '0.85rem',
    fontWeight: 500,
    '& svg': {
      width: '1.25rem',
      height: '1.25rem',
    },
  },
  smallButton: {
    cursor: 'pointer',
    width: '120px',
    margin: '0 10px 0 0',
  },
  labelPadding: {
    paddingTop: '5px',
  },
  titleNote: {
    fontSize: '0.95rem',
    lineHeight: '1.33rem',
    color: btNavyBlueColor,
  },
}));

const AddEditStaffDialog = observer((props) => {
  const {
    dialogTitle,
    instructions,
    showDialog,
    handleClose,
    saveStaff,
    selectedStaff,
    setSelectedStaff,
    type,
    allowWorkbookAccess,
    setAllowWorkbookAccess,
    staffSyncPositionClassRoleMap,
    isEmailValid,
  } = props;

  const formClasses = formStyles();
  const [emailError, setEmailError] = React.useState(false);
  const [officePhoneIsValid, setOfficePhoneIsValid] = useState(true);
  const [cellPhoneIsValid, setCellPhoneIsValid] = useState(true);

  const handleTitleSelection = (selectedStaff, pos) => {
    const position = staffSyncPositionClassRoleMap.find((p) => p.position_class === pos);
    let roleArray = [];
    roleArray.push(position.role_name);
    setSelectedStaff({
      ...selectedStaff,
      position_title: position.position_title,
      position_class: position.position_class,
      user: { ...selectedStaff.user, roles: roleArray.toString() },
    });
  };

  const handleSave = (e) => {
    e.preventDefault();

    // Validate and standardize email
    const email = selectedStaff?.user?.email || '';
    const validEmail = isEmailValid(email);

    if (email && !validEmail) {
      setEmailError(true);
      return;
    }

    setEmailError(false);
    saveStaff(e);
  };

  return (
    <React.Fragment>
      <Dialog open={showDialog} onClose={handleClose} scroll='paper' aria-labelledby='Add/Edit Staff'>
        <React.Fragment>
          <DialogTitle id='title' className={formClasses.whiteBG}>
            <div className={formClasses.dialogHeader}>
              {dialogTitle}
              <IconButton size='small' aria-label='close' color='inherit' onClick={handleClose}>
                <CloseIcon fontSize='small' />
              </IconButton>
            </div>
          </DialogTitle>
          <summary className={formClasses.dialogExplain}>{instructions}</summary>
          <div className={formClasses.dialogExplain}>
            Note: <span style={{ color: 'red' }}>*</span> indicates value is required.
          </div>
          {selectedStaff?.exclude_from_ndb_sync && (
            <div
              style={{
                textAlign: 'center',
                margin: '5px 15px 0px 15px',
                fontSize: '1.05rem',
                fontWeight: 600,
                color: btNavyBlueColor,
                fontStyle: 'italic',
              }}
            >
              This staff member is excluded from NDB Sync.
            </div>
          )}
          <DialogContent className={formClasses.dialog} style={{ width: '100%', marginTop: 20 }} align='center'>
            <Box sx={{ width: '100%' }} align='center'>
              <Grid container direction='row'>
                <Grid item xs={12} sm={5} md={3} lg={2} align='right' className={formClasses.labelPadding}>
                  <label>
                    First Name <span style={{ color: 'red' }}>*</span>
                  </label>
                </Grid>
                <Grid item xs={false} sm={1} md={1} lg={false} />
                <Grid item xs={11} sm={6} md={5} lg={5} align='left' className={formClasses.rowSpacer}>
                  <FormControl fullWidth>
                    <TextField
                      id='first-name'
                      size='small'
                      hiddenLabel
                      placeholder='First Name'
                      variant='outlined'
                      value={selectedStaff?.user?.first_name ? selectedStaff.user.first_name : ''}
                      onChange={(event) => setSelectedStaff({ ...selectedStaff, user: { ...selectedStaff.user, first_name: event.target.value } })}
                      required
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={false} sm={false} md={3} lg={4}></Grid>

                <Grid item xs={12} sm={5} md={3} lg={2} align='right' className={formClasses.labelPadding}>
                  <label>
                    Last Name <span style={{ color: 'red' }}>*</span>
                  </label>
                </Grid>
                <Grid item xs={false} sm={1} md={1} lg={false} />
                <Grid item xs={11} sm={6} md={5} lg={5} align='left' className={formClasses.rowSpacer}>
                  <FormControl fullWidth>
                    <TextField
                      id='last-name'
                      size='small'
                      hiddenLabel
                      placeholder='Last Name'
                      variant='outlined'
                      value={selectedStaff?.user?.last_name ? selectedStaff.user.last_name : ''}
                      onChange={(event) => setSelectedStaff({ ...selectedStaff, user: { ...selectedStaff.user, last_name: event.target.value } })}
                      required
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={false} sm={false} md={3} lg={4}></Grid>

                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <br />
                </Grid>

                <Grid item xs={12} sm={5} md={3} lg={2} align='right' className={formClasses.labelPadding}>
                  <label>
                    Email <span style={{ color: 'red' }}>*</span>
                  </label>
                </Grid>
                <Grid item xs={false} sm={1} md={1} lg={false} />
                <Grid item xs={11} sm={6} md={7} lg={7} align='left' className={formClasses.rowSpacer}>
                  <FormControl fullWidth>
                    <TextField
                      id='email'
                      size='small'
                      hiddenLabel
                      placeholder='Email'
                      variant='outlined'
                      value={selectedStaff?.user?.email ? selectedStaff.user.email : ''}
                      onChange={(event) => {
                        setSelectedStaff({ ...selectedStaff, user: { ...selectedStaff.user, email: event.target.value } });
                        setEmailError(false);
                      }}
                      error={emailError}
                      helperText={emailError ? 'Invalid email format' : ''}
                      required
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={false} sm={false} md={1} lg={2}></Grid>

                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <br />
                </Grid>

                <Grid item xs={12} sm={5} md={3} lg={2} align='right' className={formClasses.labelPadding}>
                  <label>PIDM</label>
                </Grid>
                <Grid item xs={false} sm={1} md={1} lg={false} />
                <Grid item xs={11} sm={6} md={5} lg={5} align='left' className={formClasses.rowSpacer}>
                  <FormControl fullWidth>
                    <TextField
                      id='pidm'
                      size='small'
                      hiddenLabel
                      placeholder='PIDM'
                      variant='outlined'
                      value={selectedStaff?.pidm ? selectedStaff.pidm : ''}
                      onChange={(event) => setSelectedStaff({ ...selectedStaff, pidm: event.target.value })}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={false} sm={false} md={3} lg={4}></Grid>

                <Grid item xs={12} sm={5} md={3} lg={2} align='right' className={formClasses.labelPadding}>
                  <label>
                    Internal ID <span style={{ color: 'red' }}>*</span>
                  </label>
                </Grid>
                <Grid item xs={false} sm={1} md={1} lg={false} />
                <Grid item xs={11} sm={6} md={5} lg={5} align='left' className={formClasses.rowSpacer}>
                  <FormControl fullWidth>
                    <TextField
                      id='internal_id'
                      size='small'
                      hiddenLabel
                      placeholder='Internal ID'
                      variant='outlined'
                      value={selectedStaff?.internal_id ? selectedStaff.internal_id : ''}
                      onChange={(event) => setSelectedStaff({ ...selectedStaff, internal_id: event.target.value.toUpperCase() })}
                      required
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={false} sm={false} md={3} lg={4}></Grid>

                <Grid item xs={12} sm={12} md={12} lg={12} align='left'>
                  <br />
                  <div className={formClasses.titleNote}>
                    Position Title selected should be the individual's title in Workday. It will be used to determine the class code and role in the
                    Successful Futures application.
                  </div>
                  <br />
                </Grid>

                <Grid item xs={12} sm={5} md={3} lg={2} align='right' className={formClasses.labelPadding}>
                  <label>
                    Position Title <span style={{ color: 'red' }}>*</span>
                  </label>
                </Grid>
                <Grid item xs={false} sm={1} md={1} lg={false} />
                <Grid item xs={11} sm={6} md={8} lg={8} align='left' className={formClasses.rowSpacer}>
                  <FormControl fullWidth>
                    <Select
                      labelId='position-title-label'
                      id='position_title'
                      value={selectedStaff?.position_class ? selectedStaff.position_class : ''}
                      placeholder='Position Title'
                      onChange={(event) => handleTitleSelection(selectedStaff, event.target.value)}
                      style={{ padding: '0 15px' }}
                      required
                    >
                      <MenuItem value={''}></MenuItem>
                      {staffSyncPositionClassRoleMap
                        .slice()
                        .sort((a, b) => {
                          return a.position_title > b.position_title ? 1 : -1;
                        })
                        .map((pos) => {
                          return (
                            <MenuItem key={pos.id} value={pos.position_class}>
                              {pos.position_title}
                            </MenuItem>
                          );
                        })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={false} sm={false} md={false} lg={1}></Grid>

                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <br />
                </Grid>

                <Grid item xs={12} sm={5} md={3} lg={2} align='right' className={formClasses.labelPadding}>
                  <label>Office Phone</label>
                </Grid>
                <Grid item xs={false} sm={1} md={1} lg={false} />
                <Grid item xs={11} sm={6} md={5} lg={5} align='left' className={formClasses.rowSpacer}>
                  <FormControl fullWidth error={!officePhoneIsValid}>
                    <PhoneInput
                      id='phone'
                      variant='outlined'
                      value={selectedStaff?.office_phone}
                      required
                      handleChange={(val, isValid) => {
                        setSelectedStaff({ ...selectedStaff, office_phone: val.replace(/\D/g, '') });
                        setOfficePhoneIsValid(isValid);
                      }}
                    />
                    <FormHelperText id='my-helper-text'>{!officePhoneIsValid && 'Invalid office phone number.'}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={false} sm={false} md={3} lg={4}></Grid>

                <Grid item xs={12} sm={5} md={3} lg={2} align='right' className={formClasses.labelPadding}>
                  <label>Cell Phone</label>
                </Grid>
                <Grid item xs={false} sm={1} md={1} lg={false} />
                <Grid item xs={11} sm={6} md={5} lg={5} align='left' className={formClasses.rowSpacer}>
                  <FormControl fullWidth error={!cellPhoneIsValid}>
                    <PhoneInput
                      id='phone'
                      variant='outlined'
                      value={selectedStaff?.cell_phone}
                      handleChange={(val, isValid) => {
                        setSelectedStaff({ ...selectedStaff, cell_phone: val.replace(/\D/g, '') });
                        setCellPhoneIsValid(isValid);
                      }}
                    />
                    <FormHelperText id='my-helper-text'>{!cellPhoneIsValid && 'Invalid cell phone number.'}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={false} sm={false} md={3} lg={4}></Grid>

                {type === 'EDIT' && (
                  <>
                    <Grid container direction='row' style={{ marginTop: 20 }}>
                      <Grid item xs={false} sm={6} md={4} lg={3} />
                      <Grid item xs={12} sm={6} md={6} lg={9} align='left'>
                        <FormControlLabel
                          control={<Checkbox color='default' />}
                          label='Active'
                          onChange={(event) =>
                            setSelectedStaff({
                              ...selectedStaff,
                              is_active: event.target.checked,
                              user: { ...selectedStaff.user, is_active: event.target.checked },
                            })
                          }
                          checked={selectedStaff?.is_active ? true : false}
                        />
                      </Grid>

                      <Grid item xs={false} sm={6} md={4} lg={3} />
                      <Grid item xs={12} sm={6} md={6} lg={9} align='left'>
                        <FormControlLabel
                          control={<Checkbox color='default' />}
                          label='Planning Workbook Access'
                          onChange={(event) => setAllowWorkbookAccess(event.target.checked)}
                          checked={allowWorkbookAccess}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  style={{ margin: '30px 0 30px 0', display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'flex-end' }}
                >
                  <Button className={formClasses.smallButton} color='default' variant='outlined' round onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button
                    className={formClasses.smallButton}
                    color='primary'
                    variant='contained'
                    round
                    onClick={handleSave}
                    disabled={(selectedStaff?.office_phone && !officePhoneIsValid) || (selectedStaff?.cell_phone && !cellPhoneIsValid)}
                  >
                    Save
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
        </React.Fragment>
      </Dialog>
    </React.Fragment>
  );
});

export default AddEditStaffDialog;

AddEditStaffDialog.propTypes = {
  dialogTitle: PropTypes.string.isRequired,
  instructions: PropTypes.string,
  showDialog: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  saveStaff: PropTypes.func.isRequired,
  selectedStaff: PropTypes.object.isRequired,
  setSelectedStaff: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  allowWorkbookAccess: PropTypes.bool.isRequired,
  setAllowWorkbookAccess: PropTypes.func.isRequired,
  staffSyncPositionClassRoleMap: PropTypes.array.isRequired,
  isEmailValid: PropTypes.func.isRequired,
};
